import { useState, FC } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InfiniteScrollMultiSelect,
} from '@features/ui';
import { useTradingUserInfiniteOptions } from '@hooks/useTradingUserInfiniteOptions';

export type TCreateCodeFormPayload = {
  count: number;
  prefix: string;
  assigned_user_ids: number[];
};

interface CreateCodeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: TCreateCodeFormPayload) => Promise<void>;
}

export const CreateCodeForm: FC<CreateCodeFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [count, setCount] = useState(1);
  const [prefix, setPrefix] = useState('');
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const {
    userOptions,
    loading: usersLoading,
    hasMore,
    loadMore,
  } = useTradingUserInfiniteOptions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        count,
        prefix,
        assigned_user_ids: assignedUserIds,
      });
      onOpenChange(false);
      setCount(1);
      setPrefix('');
      setAssignedUserIds([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Create Coupon Code</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Count */}
          <div>
            <label
              className="block text-sm font-medium mb-1"
              htmlFor="cc-count"
            >
              Count
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Number of coupon codes to generate
            </p>
            <input
              className="border border-gray-300 rounded px-3 py-2 w-full max-w-[120px]"
              id="cc-count"
              min={1}
              type="number"
              value={count}
              onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
            />
          </div>

          {/* Code (maps to "prefix" in API payload) */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="cc-code">
              Code <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Leave empty to let the system generate a code automatically
            </p>
            <input
              className="border border-gray-300 rounded px-3 py-2 w-full max-w-[260px]"
              id="cc-code"
              placeholder="e.g. SUMMER25"
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
            />
          </div>

          {/* Users */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Users{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Assign specific users who can use this coupon
            </p>
            <InfiniteScrollMultiSelect
              isMulti
              hasMore={hasMore}
              isLoading={usersLoading}
              options={userOptions}
              placeholder="Select users..."
              value={assignedUserIds.map(String)}
              onChange={(v) => {
                const ids = Array.isArray(v)
                  ? v.map((x) => Number(x)).filter(Boolean)
                  : [];

                setAssignedUserIds(ids);
              }}
              onMenuScrollToBottom={loadMore}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={submitting}
              size="sm"
              type="submit"
              variant="lime"
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
